import { Component, effect, inject, input, signal } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import { DynamicEntityDetail, DynamicEntityVersion } from './dynamic.models';

@Component({
  standalone: true,
  selector: 'ain-dynamic-version-history',
  imports: [
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    TagModule,
    AinDatePipe
  ],
  template: `
    <section class="version-history">
      <div class="section-header">
        <h3>{{ t('dynamic.version.list') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.version.info.title'"
          [descriptionKey]="'dynamic.version.info.description'"
          [bulletKeys]="[
            'dynamic.version.info.bullets.history',
            'dynamic.version.info.bullets.rollback'
          ]" />
      </div>

      @if (!canViewVersions()) {
        <ain-state type="empty" [title]="t('dynamic.version.title')" [message]="t('common.noPermission')" />
      } @else if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!versions().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="versions()" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('dynamic.version.number') }}</th>
              <th>{{ t('dynamic.version.tableName') }}</th>
              <th>{{ t('dynamic.version.changeDescription') }}</th>
              <th>{{ t('dynamic.version.isActive') }}</th>
              <th>{{ t('dynamic.version.createdAt') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-version>
            <tr [class.active-version-row]="version.isActive">
              <td>{{ version.version }}</td>
              <td><code>{{ version.tableName }}</code></td>
              <td>{{ version.changeDescription || '—' }}</td>
              <td>
                @if (version.isActive) {
                  <p-tag [value]="t('dynamic.version.activeTag')" severity="success" />
                } @else {
                  <span class="muted">—</span>
                }
              </td>
              <td>{{ version.createdAtUtc | ainDate:'datetime' }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </section>
  `,
  styles: [`
    .version-history { display: grid; gap: 12px; }
    .section-header { display: grid; gap: 8px; }
    .section-header h3 { margin: 0; font-size: 1.05rem; }
    .active-version-row { background: #ecfdf5; }
    .muted { color: #9ca3af; }
    code { font-size: 0.92em; }
  `]
})
export class DynamicVersionHistoryComponent {
  readonly entity = input.required<DynamicEntityDetail>();

  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);

  readonly versions = signal<DynamicEntityVersion[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  constructor() {
    effect(() => {
      const ent = this.entity();
      if (ent?.id) {
        this.loadVersions(ent.id);
      }
    });
  }

  canViewVersions(): boolean {
    return this.hasEntityAction('View');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadVersions(entityId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.listVersions(entityId).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: items => this.versions.set(items),
      error: err => {
        this.versions.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  private hasEntityAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.entities');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }
}
